import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { getGridStringOperators } from "@mui/x-data-grid";
import { useEffect, useState, useCallback, useContext } from "react";
import { HiTemplate } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import usePermission from "../../hooks/usePermission";
import PermissionGate from "../../components/PermissionGate";
import NodataImg from "../../assets/images/noData.png";
import Appbar from "../../components/Appbar";
import Body from "../../components/Body";
import Drawer from "../../components/Drawer";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ForumIcon from '@mui/icons-material/Forum';
import { useNavigate } from "react-router-dom";
import { reset } from "../../store/courses/courseDetails";
import { courseApi } from "../../store/courses/coursesList";
import DeleteCourse from "./DeleteCourse";
import PublishCourse from "./PublishCourse";
import { ThemeContext } from '../../context/ThemeContext';
import CustomGridToolbar from "../../components/CustomGridToolbar";
import apiRoutes from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import { resetSection } from "../../store/courses/getSection";
import DataGridComponent from "../../components/DataGridComponent";
import constants from "../../const"
const getKeyTakeawaysMarkup = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  // The API may return a fragment or a complete HTML document. Rendering the
  // document body keeps either format consistent inside the grid cell.
  if (typeof DOMParser === "undefined") {
    return String(value);
  }

  const parsedDocument = new DOMParser().parseFromString(String(value), "text/html");
  const body = parsedDocument.body;

  if (!body) {
    return String(value);
  }

  body.querySelectorAll("script, style, iframe, object, embed, form, input, button, link, meta")
    .forEach((element) => element.remove());

  body.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach(({ name, value: attributeValue }) => {
      if (name === "style" || name === "class" || name === "id" || /^on/i.test(name)) {
        element.removeAttribute(name);
      } else if (["href", "src", "action"].includes(name) && /^javascript:/i.test(attributeValue.trim())) {
        element.removeAttribute(name);
      }
    });
  });

  const textContent = body.textContent.trim();
  const hasVisibleContent = textContent || body.querySelector("img, video");

  if (!hasVisibleContent) {
    return null;
  }

  if (textContent.length > constants.KEY_TAKEAWAYS_DISPLAY_LIMIT) {
    return {
      markup: `${textContent.slice(0, constants.KEY_TAKEAWAYS_DISPLAY_LIMIT).trimEnd()}…`,
      fullText: textContent,
    };
  }

  return { markup: body.innerHTML, fullText: textContent };
};

export default function Courses() {

  const courses = useSelector((state) => state?.courses);
  const { result, loading, total } = courses;
  const [deleteId, setDeleteId] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDownload, setIsDownload] = useState(false)
  const { requirePermission } = usePermission();

  const [selectedRow, setSelectedRow] = useState();
  const [notSelectedProductModel, setNotSelectedProductModel] = useState(false);
  const { paginationModel, setPaginationModel } = useContext(ThemeContext)
  const [queryOptions, setQueryOptions] = useState({});

  const onFilterChange = useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
  }, []);
  const columns = [
    {
      field: "course_id",
      headerName: "Course Id",
      filterable: false,
      sortable: false,
      width: 150,
    },
    {
      field: "name",
      headerName: "Content Name",
      filterable: true,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === 'equals' | operator.value === 'contains',
      ),
      sortable: false,
      width: 150,
    },
    {
      field: "start_display",
      headerName: "Starts",
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: (rowData) => (
        <>
          <Typography>{new Date(rowData.row.start).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}</Typography>
        </>
      ),
    },
    {
      field: "key_takeaways",
      headerName: "Key Takeaways",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: ({ value }) => {
        const content = getKeyTakeawaysMarkup(value);

        if (!content) {
          return <span className="key-takeaways-empty">—</span>;
        }

        return (
          <div
            className="key-takeaways-content"
            title={content.fullText}
            dangerouslySetInnerHTML={{ __html: content.markup }}
          />
        );
      },
    },
    {
      field: "org",
      headerName: "Organisation",
      filterable: false,
      sortable: false,
      width: 150,
    },
    {
      field: "number",
      headerName: "Course Number",
      filterable: false,
      sortable: false,
      width: 150,
    },
    {
      field: "category",
      headerName: "Category",
      filterable: true,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === 'equals',
      ),
      sortable: false,
      width: 150,
    },
    {
      field: "pacing",
      headerName: "Pacing Type",
      filterable: false,
      sortable: false,
      width: 150,
    },
    {
      field: "blocks_url",
      headerName: "Blocks Url",
      filterable: false,
      sortable: false,
      width: 300,
    },
    {
      field: "publish",
      headerName: "Publish Course",
      filterable: false,
      sortable: false,
      width: 150,
      disableExport: true,
      renderCell: (row) => (
        <>
          <PermissionGate module="courses" action={row.row.published ? "unpublish" : "publish"}>
            <Button
              color="info"
              onClick={() => {
                setNotSelectedProductModel(true)
                setDeleteId(row.id)
                setSelectedRow(row.row)
              }}
              endIcon={<ArrowRightAltIcon />}>
              {row.row.published ? "Unpublish" : "Publish"}
            </Button>
          </PermissionGate>
        </>
      ),
    },
    {
      field: "update_course",
      headerName: "Update Course",
      filterable: false,
      sortable: false,
      width: 150,
      disableExport: true,
      renderCell: (row) => (
        <>
          <PermissionGate module="courses" action="update">
            <Button
              color="info"
              endIcon={<EditIcon />}
              label="Available" variant="outlined"
              onClick={() => navigate(`/edit-course/${row.row.course_id}`)}
            >
              Edit
            </Button>
          </PermissionGate>
        </>
      ),
    },
    {
      field: "discussion",
      headerName: "Discussion",
      filterable: false,
      sortable: false,
      width: 150,
      disableExport: true,
      renderCell: (row) => (
        <>
          <PermissionGate module="courses" action="update">
            <Button
              color="info"
              endIcon={<ForumIcon />}
              label="Available" variant="outlined"
              onClick={() => navigate(`/courses/discussion/${row.row.course_id}`)}
            >
              Discussion
            </Button>
          </PermissionGate>
        </>
      ),
    }
  ];



  const addToCartHandler = async () => {
    navigate("/add-courses")
  };

  useEffect(() => {
    const filterModel = queryOptions?.filterModel;
    const items = filterModel?.items;
    const firstItem = items && items.length > 0 ? items[0] : {};
    const quickFilterValues = filterModel?.quickFilterValues;

    const query = {
      page: paginationModel?.page + 1,
      page_size: paginationModel?.pageSize,
      field: firstItem.field || '',
      operator: firstItem.operator || (quickFilterValues && "contains") || '',
      value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
    };
    dispatch(courseApi(query));
    dispatch(reset());
    dispatch(resetSection())

  }, [dispatch, queryOptions, paginationModel]);

  return (
    <>
      <Appbar />
      <Drawer />
      <Body>


        <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
          <HiTemplate size={30} />
          <Typography>Courses List</Typography>
        </Stack>

        <Stack sx={{ my: 2 }} justifyContent="space-between" direction="row">
          <Stack direction="row" spacing={1}>
            <PermissionGate module="courses" action="create">
              <Button
                onClick={addToCartHandler}
                variant="contained">
                ADD New Course
              </Button>
            </PermissionGate>
          </Stack>
        </Stack>

        <Paper elevation={0}>
          <DataGridComponent
            loading={loading}
            rowCount={total}
            pageSizeOptions={[5, 10, 20, 30]}
            pageSize={paginationModel?.pageSize}
            onPageSizeChange={(newPageSize) => {
              setPaginationModel({
                ...paginationModel,
                pageSize: newPageSize,
              })
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{
              height: 500,
              "& .key-takeaways-content": {
                width: "100%",
                minWidth: 0,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: 1.4,
                "& > :first-child": {
                  marginTop: 0,
                },
                "& > :last-child": {
                  marginBottom: 0,
                },
                "& p, & ul, & ol, & blockquote, & pre": {
                  marginTop: "0.35em",
                  marginBottom: "0.35em",
                },
                "& ul, & ol": {
                  paddingLeft: "1.25em",
                },
                "& img, & video": {
                  maxWidth: "100%",
                  height: "auto",
                },
                "& pre": {
                  whiteSpace: "pre-wrap",
                },
              },
              "& .key-takeaways-empty": {
                color: "text.secondary",
              },
            }}
            getRowId={(row) => row.id}
            rows={result}
            columns={columns}
            onFilterModelChange={onFilterChange}
            slots={{
              noRowsOverlay: () => (
                <Box className="noData">
                  <img src={NodataImg} alt="no data found" />
                </Box>
              ),
              toolbar: (props) =>
                (CustomGridToolbar({ ...props, moduleType: "coursesManagement", queryOptions: queryOptions, paginationModel: paginationModel, isDownload: isDownload, setIsDownload: setIsDownload, apiUrl: apiRoutes.courseList, permissionModule: "courses" })),
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'linear-progress',
              },
              toolbar: {
                showQuickFilter: true,
              },
            }}
          />
        </Paper>


      </Body>
      <PublishCourse
        open={notSelectedProductModel}
        setOpen={setNotSelectedProductModel}
        selectedRow={selectedRow}
        id={deleteId}
      />
      <BackdropUI open={isDownload} />
    </>
  );
}
